import React, { useState, useEffect } from 'react';
import { Copy, Download, Lock, Globe, Menu, Check } from 'lucide-react';
import '../styles/api-docs.css';

const ApiDocs = ({ language }) => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [activeLang, setActiveLang] = useState('curl');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const element = document.getElementById(activeSection);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeSection]);

  const sidebarSections = [
    { 
      id: 'introduction', 
      title: language === 'en' ? 'Introduction' : 'مقدمة',
      subsections: []
    },
    { 
      id: 'authentication', 
      title: language === 'en' ? 'Authentication' : 'المصادقة',
      subsections: []
    },
    { 
      id: 'endpoints', 
      title: language === 'en' ? 'Endpoints' : 'نقاط النهاية',
      subsections: [
        { id: 'health', title: 'Health Check', method: 'GET', auth: false },
        { id: 'send-otp-v2', title: 'Send OTP V2 ', method: 'POST', auth: true },
        { id: 'status', title: 'Check Status', method: 'GET', auth: true },
        { id: 'logs', title: 'Get Logs', method: 'GET', auth: true }
      ]
    },
    { 
      id: 'errors', 
      title: language === 'en' ? 'Error Codes' : 'رموز الخطأ',
      subsections: []
    }
  ];

  const endpoints = {
    health: {
      method: 'GET',
      path: '/v1/health',
      title: language === 'en' ? 'Health Check' : 'فحص الصحة',
      description: language === 'en' 
        ? 'Check if the WhatsApp client is ready and connected. No authentication required.'
        : 'تحقق مما إذا كان عميل WhatsApp جاهزًا ومتصلاً. لا يتطلب مصادقة.',
      auth: false,
      requestBody: null,
      responseExample: {
        success: true,
        status: 'ready',
        error: null
      },
      examples: {
        curl: 'curl -X GET "https://safrjal.com/api/v1/health"',
        js: `fetch('https://safrjal.com/api/v1/health')
  .then(res => res.json())
  .then(data => console.log(data));`,
        python: `import requests
response = requests.get('https://safrjal.com/api/v1/health')
print(response.json())`
      }
    },
    'send-otp-v2': {
      method: 'POST',
      path: '/v2/send-otp',
      title: language === 'en' ? 'Send OTP V2 ' : 'إرسال OTP V2 (مع اسم التطبيق)',
      description: language === 'en'
        ? 'Send OTP with automatic app title blending. The title is integrated into 8 different message formats to prevent bot detection.'
        : 'إرسال OTP مع دمج اسم التطبيق تلقائياً. يتم دمج العنوان في 8 تنسيقات رسائل مختلفة لمنع كشف الروبوت.',
      auth: true,
      requestBody: {
        phone: { type: 'string', required: true, example: '962795021746', description: 'Phone number' },
        otp: { type: 'string', required: true, example: '4850', description: 'OTP code' },
        title: { type: 'string', required: false, example: 'ودني', description: 'App/Website name (optional)' }
      },
      responseExample: {
        success: true,
        id: '1703087942000',
        message: 'OTP sent successfully',
        remaining: 199
      },
      examples: {
        curl: `curl -X POST "https://safrjal.com/api/v2/send-otp" -H "X-API-Key: YOUR_API_KEY" -H "Content-Type: application/json" -d '{"phone": "962795021746", "otp": "4850", "title": "ودني"}'`,
        js: `fetch('https://safrjal.com/api/v2/send-otp', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    phone: '962795021746',
    otp: '4850',
    title: 'ودني'
  })
})
  .then(res => res.json())
  .then(data => console.log(data));`,
        python: `import requests

response = requests.post(
  'https://safrjal.com/api/v2/send-otp',
  headers={'X-API-Key': 'YOUR_API_KEY'},
  json={
    'phone': '962795021746',
    'otp': '4850',
    'title': 'ودني'
  }
)
print(response.json())`
      }
    },
    status: {
      method: 'GET',
      path: '/v1/status/{id}',
      title: language === 'en' ? 'Check Status' : 'التحقق من الحالة',
      description: language === 'en'
        ? 'Check the delivery status of a previously sent OTP using its message ID.'
        : 'التحقق من حالة إرسال OTP السابق باستخدام معرف الرسالة.',
      auth: true,
      requestBody: null,
      responseExample: {
        success: true,
        data: {
          id: '1699560123456',
          phone: '962795021746',
          timestamp: '2024-11-09T10:30:45.123Z',
          status: 'sent'
        }
      },
      examples: {
        curl: `curl -X GET "https://safrjal.com/api/v1/status/1699560123456" -H "X-API-Key: YOUR_API_KEY"`,
        js: `fetch('https://safrjal.com/api/v1/status/1699560123456', {
  headers: {'X-API-Key': 'YOUR_API_KEY'}
})
  .then(res => res.json())
  .then(data => console.log(data));`,
        python: `import requests

response = requests.get(
  'https://safrjal.com/api/v1/status/1699560123456',
  headers={'X-API-Key': 'YOUR_API_KEY'}
)
print(response.json())`
      }
    },
    logs: {
      method: 'GET',
      path: '/v1/logs',
      title: language === 'en' ? 'Get Logs' : 'الحصول على السجلات',
      description: language === 'en'
        ? 'Retrieve OTP delivery log entries. Supports pagination and filtering.'
        : 'استرجع إدخالات سجل إرسال OTP. يدعم الترقيم والتصفية.',
      auth: true,
      requestBody: null,
      responseExample: {
        success: true,
        count: 42,
        logs: [
          {
            id: '1699560123456',
            phone: '962795021746',
            timestamp: '2024-11-09T10:30:45.123Z',
            status: 'sent'
          }
        ]
      },
      examples: {
        curl: `curl -X GET "https://safrjal.com/api/v1/logs?limit=50" -H "X-API-Key: YOUR_API_KEY"`,
        js: `fetch('https://safrjal.com/api/v1/logs?limit=50', {
  headers: {'X-API-Key': 'YOUR_API_KEY'}
})
  .then(res => res.json())
  .then(data => console.log(data));`,
        python: `import requests

response = requests.get(
  'https://safrjal.com/api/v1/logs',
  headers={'X-API-Key': 'YOUR_API_KEY'},
  params={'limit': 50}
)
print(response.json())`
      }
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const downloadCollection = () => {
    const link = document.createElement('a');
    link.href = '/otp-api-postman-collection.json';
    link.download = 'otp-api-postman-collection.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="api-docs-layout">
      {/* Sidebar */}
      <aside className={`api-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="api-sidebar-header">
          <h2>{language === 'en' ? 'API Reference' : 'مرجع API'}</h2>
        </div>

        <nav className="api-sidebar-nav">
          {sidebarSections.map((section) => (
            <div key={section.id} className="nav-section">
              <button
                className={`nav-section-title ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveSection(section.id);
                  // Auto-close sidebar on mobile when a section is clicked
                  if (window.innerWidth <= 768) {
                    setSidebarOpen(false);
                  }
                }}
              >
                {section.title}
              </button>
              {section.subsections.length > 0 && (
                <div className="nav-subsections">
                  {section.subsections.map((sub) => (
                    <button
                      key={sub.id}
                      className={`nav-subsection ${activeSection === sub.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveSection(sub.id);
                        // Auto-close sidebar on mobile when a subsection is clicked
                        if (window.innerWidth <= 768) {
                          setSidebarOpen(false);
                        }
                      }}
                    >
                      <span className={`method-badge ${sub.method.toLowerCase()}`}>
                        {sub.method}
                      </span>
                      <span className="endpoint-title">{sub.title}</span>
                      {sub.auth && <Lock size={12} className="auth-icon" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="api-sidebar-footer">
          <button className="download-spec-btn-sidebar" onClick={downloadCollection}>
            <Download size={16} />
            {language === 'en' ? 'Download Collection' : 'تحميل المجموعة'}
          </button>
        </div>
      </aside>

      {/* Mobile Toggle */}
      <button className="mobile-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} title={sidebarOpen ? 'Close menu' : 'Open menu'}>
        <Menu size={24} />
      </button>

      {/* Main Content */}
      <main className="api-docs-content">
        {/* Introduction Section */}
        <section id="introduction" className="docs-section">
          <h1>{language === 'en' ? 'API Documentation' : 'توثيق API'}</h1>
          <p className="lead">
            {language === 'en' 
              ? 'Professional WhatsApp OTP Delivery API - Get started in minutes'
              : 'واجهة برمجة تطبيقات احترافية لإرسال OTP عبر WhatsApp - ابدأ في دقائق'}
          </p>

          <div className="quick-start-cards">
            <div className="quick-card">
              <div className="step-num">1</div>
              <h3>{language === 'en' ? 'Get API Key' : 'احصل على مفتاح API'}</h3>
              <p>{language === 'en' ? 'Generate your API key from the dashboard' : 'أنشئ مفتاح API من لوحة التحكم'}</p>
            </div>
            <div className="quick-card">
              <div className="step-num">2</div>
              <h3>{language === 'en' ? 'Add to Headers' : 'أضف إلى الرؤوس'}</h3>
              <p>{language === 'en' ? 'Include X-API-Key in request headers' : 'قم بتضمين X-API-Key في رؤوس الطلب'}</p>
            </div>
            <div className="quick-card">
              <div className="step-num">3</div>
              <h3>{language === 'en' ? 'Make Request' : 'قدم الطلب'}</h3>
              <p>{language === 'en' ? 'Call any endpoint with required parameters' : 'استدع أي نقطة نهاية بالمعاملات المطلوبة'}</p>
            </div>
          </div>

          <div className="info-box">
            <h4>{language === 'en' ? 'Base URL' : 'عنوان URL الأساسي'}</h4>
            <code className="inline-code">https://safrjal.com/api</code>
          </div>
        </section>

        {/* Authentication Section */}
        <section id="authentication" className="docs-section">
          <h2>{language === 'en' ? 'Authentication' : 'المصادقة'}</h2>
          <p>
            {language === 'en'
              ? 'All API requests require an API key to be included in the request header:'
              : 'جميع طلبات API تتطلب مفتاح API يتم تضمينه في رأس الطلب:'}
          </p>
          
          <div className="code-example">
            <div className="code-header">
              <span>{language === 'en' ? 'Header' : 'رأس'}</span>
              <button 
                className="copy-code-btn"
                onClick={() => copyToClipboard('X-API-Key: YOUR_API_KEY', 'auth')}
              >
                {copiedIndex === 'auth' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre className="code-block"><code>X-API-Key: YOUR_API_KEY</code></pre>
          </div>

          <p>
            {language === 'en'
              ? 'Generate your API key from your dashboard under the API Keys section.'
              : 'قم بإنشاء مفتاح API من لوحة التحكم الخاصة بك ضمن قسم مفاتيح API.'}
          </p>
        </section>

        {/* Endpoints Header */}
        <section id="endpoints" className="docs-section">
          <h2>{language === 'en' ? 'API Endpoints' : 'نقاط نهاية API'}</h2>
          <p>{language === 'en' ? 'Available endpoints for OTP delivery and management.' : 'نقاط النهاية المتاحة لإرسال وإدارة OTP.'}</p>
        </section>

        {/* Individual Endpoint Sections */}
        {Object.entries(endpoints).map(([key, endpoint]) => (
          <section id={key} key={key} className="docs-section endpoint-section">
            <div className="endpoint-header-inline">
              <span className={`method-badge ${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
              <code className="endpoint-path">{endpoint.path}</code>
              {endpoint.auth ? (
                <span className="auth-badge-inline">
                  <Lock size={14} />
                  {language === 'en' ? 'Auth Required' : 'يتطلب المصادقة'}
                </span>
              ) : (
                <span className="public-badge-inline">
                  <Globe size={14} />
                  {language === 'en' ? 'Public' : 'عام'}
                </span>
              )}
            </div>

            <h3>{endpoint.title}</h3>
            <p>{endpoint.description}</p>

            {endpoint.requestBody && (
              <div className="params-section">
                <h4>{language === 'en' ? 'Request Parameters' : 'معاملات الطلب'}</h4>
                <table className="params-table">
                  <thead>
                    <tr>
                      <th>{language === 'en' ? 'Parameter' : 'المعامل'}</th>
                      <th>{language === 'en' ? 'Type' : 'النوع'}</th>
                      <th>{language === 'en' ? 'Required' : 'مطلوب'}</th>
                      <th>{language === 'en' ? 'Description' : 'الوصف'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(endpoint.requestBody).map(([paramKey, param]) => (
                      <tr key={paramKey}>
                        <td><code>{paramKey}</code></td>
                        <td><code>{param.type}</code></td>
                        <td>{param.required ? '✓' : '-'}</td>
                        <td>{param.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="response-section">
              <h4>{language === 'en' ? 'Response' : 'الاستجابة'}</h4>
              <div className="code-example">
                <div className="code-header">
                  <span>JSON</span>
                  <button 
                    className="copy-code-btn"
                    onClick={() => copyToClipboard(JSON.stringify(endpoint.responseExample, null, 2), `response-${key}`)}
                  >
                    {copiedIndex === `response-${key}` ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <pre className="code-block"><code>{JSON.stringify(endpoint.responseExample, null, 2)}</code></pre>
              </div>
            </div>

            <div className="examples-section">
              <h4>{language === 'en' ? 'Code Examples' : 'أمثلة الكود'}</h4>
              <div className="lang-tabs">
                <button
                  className={`lang-tab ${activeLang === 'curl' ? 'active' : ''}`}
                  onClick={() => setActiveLang('curl')}
                >
                  cURL
                </button>
                <button
                  className={`lang-tab ${activeLang === 'js' ? 'active' : ''}`}
                  onClick={() => setActiveLang('js')}
                >
                  JavaScript
                </button>
                <button
                  className={`lang-tab ${activeLang === 'python' ? 'active' : ''}`}
                  onClick={() => setActiveLang('python')}
                >
                  Python
                </button>
              </div>
              <div className="code-example">
                <div className="code-header">
                  <span>{activeLang.toUpperCase()}</span>
                  <button 
                    className="copy-code-btn"
                    onClick={() => copyToClipboard(endpoint.examples[activeLang], `example-${key}`)}
                  >
                    {copiedIndex === `example-${key}` ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <pre className="code-block"><code>{endpoint.examples[activeLang]}</code></pre>
              </div>
            </div>
          </section>
        ))}

        {/* Error Codes Section */}
        <section id="errors" className="docs-section">
          <h2>{language === 'en' ? 'Error Codes' : 'رموز الخطأ'}</h2>
          <p>{language === 'en' ? 'Common HTTP status codes you may encounter:' : 'رموز حالة HTTP الشائعة التي قد تواجهها:'}</p>
          
          <div className="error-codes-grid">
            <div className="error-code-card">
              <span className="error-code">400</span>
              <p>{language === 'en' ? 'Bad Request - Missing or invalid parameters' : 'طلب سيء - معاملات مفقودة أو غير صحيحة'}</p>
            </div>
            <div className="error-code-card">
              <span className="error-code">401</span>
              <p>{language === 'en' ? 'Unauthorized - Invalid or missing API key' : 'غير مصرح - مفتاح API غير صحيح أو مفقود'}</p>
            </div>
            <div className="error-code-card">
              <span className="error-code">404</span>
              <p>{language === 'en' ? 'Not Found - Resource not found' : 'غير موجود - المورد غير موجود'}</p>
            </div>
            <div className="error-code-card">
              <span className="error-code">429</span>
              <p>{language === 'en' ? 'Rate Limited - Too many requests' : 'معدل محدود - عدد طلبات كثير جداً'}</p>
            </div>
            <div className="error-code-card">
              <span className="error-code">503</span>
              <p>{language === 'en' ? 'Service Unavailable' : 'الخدمة غير متاحة - عميل WhatsApp غير جاهز'}</p>
            </div>
          </div>
        </section>

        {/* Support Section */}
        <section className="docs-section support-section">
          <h2>{language === 'en' ? 'Need Help?' : 'هل تحتاج إلى مساعدة؟'}</h2>
          <p>{language === 'en' ? 'Contact our support team:' : 'اتصل بفريق الدعم:'}</p>
          <div className="support-links">
            <a href="mailto:safjral.solutions@gmail.com" className="support-link">
              {language === 'en' ? 'Email Support' : 'دعم البريد الإلكتروني'}
            </a>
            <span className="support-link support-link-disabled" style={{cursor: 'not-allowed', opacity: 0.6}}>
              {language === 'en' ? 'Telegram' : 'تيليجرام'}
            </span>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ApiDocs;
